import Immutable from 'immutable';
import { pad } from './date';
import { pageContext } from './page_context';

/**
 * Returns the value with the GST added
 * @param {Number|String} value - the number value that you want to add the GST to
 * @returns {String} - The value with GST added to two decimal places
 */
const applyGST = (value) => {
    if (!isNaN(parseFloat(value)) && !isNaN(value - 0)) {
        return (value * 1.1).toFixed(2);
    }
    throw Error(`the value passed: ${value} is not a number`);
};

/**
 * This is only to be used for testing.
 *
 * The actual getAge function will always return an age that increases with time, which breaks our snapshot tests,
 * this function allows us to call getAge with a fixed date for 'today', so that the age will always be the same
 * when used in unit tests.
 * @param {Object} args - The date of birth
 * @param {String} args.actionValue - The action value
 * @param {Object} args.state - The state
 * @returns {Number} - The number of years since dob
 */
const mockGetAge = ({ actionValue, state }) => {
    // eslint-disable-next-line no-console
    console.warn('unit test only function mockGetAge() was called');
    const today = new Date('2017-06-21');
    return getAge({ state, actionValue }, today);
};

/**
 * Returns the number of years between today and a given date.
 * @param {Object} args - The arguments
 * @param {Object} args.state - The date of birth
 * @param {String} args.actionValue - The date of birth
 * @param {Date} today - A fixed date to be used for testing only
 * @returns {Number} - The number of years since dob
 */
const getAge = ({ state, actionValue }, today = null) => {
    if (today === null) {
        // eslint-disable-next-line no-param-reassign
        today = new Date();
    }

    const dob = state.getIn(actionValue.split('.'));
    const dobDate = new Date(dob);
    if (dob === null || isNaN(dobDate.getDate())) {
        return NaN;
    }

    const thisYear = today.getFullYear();
    const dobYear = dobDate.getFullYear();

    const thisMonth = today.getMonth() + 1;
    const dobMonth = dobDate.getMonth() + 1;

    const thisDay = today.getDate();
    const dobDay = dobDate.getDate();

    let age = thisYear - dobYear;
    if (thisMonth < dobMonth || (thisMonth === dobMonth && thisDay < dobDay)) {
        age -= 1;
    }

    return age;
};

/**
 * Returns the timestamp (ms since epoch) for a given date string from state
 * @param {Object} args - The arguments
 * @param {Object} args.state - The state
 * @param {String} args.actionValue - The state path to the date string
 *   @example vertical.quote.form.coverDetail.driver.dob
 * @returns {Number} - A numeric timestamp
 */
const getDateValue = ({ state, actionValue }) => {
    const dateStr = state.getIn(actionValue.split('.'));
    const date = new Date(dateStr);

    if (!dateStr || isNaN(date.getTime())) {
        return NaN;
    }

    return date.getTime();
};

/**
 * Convert a string input value to a number
 * @param {Object} args - The arguments
 * @param {Immutable.Map} args.state - The state map
 * @param {String} args.actionValue - The state key
 * @returns {Number} A number or 0 if the input is not a number.
 */
const getNumber = ({ state, actionValue }) => {
    let value = state.getIn(actionValue.split('.'));
    value = typeof value !== 'number' ? parseFloat(value) : value;
    return isNaN(value) ? 0 : value;
};

/**
 * Returns sum of an array where the values are stringified numbers
 * @param {Object} args - The arguments
 * @param {Array} args.value - The array of values to sum
 * @returns {Number} - The sum of values
 */
const getSum = ({ value }) => {
    return value.reduce((total, current) => total + parseFloat(current || 0), 0);
};

/**
 * currentMonth transform - return the current month for restricting radio options etc for months to those less than or
 * equal to the current month
 * @param {Date} currentDate A date object to pass in (for unit testing)
 * @returns {Number} - The current month index (1-12)
 */
const currentMonth = (currentDate = new Date()) => currentDate.getMonth() + 1;

/**
 * Return the name of the month for the given month number eg: 1 == January
 * @param {Object} args - The number of the month, between 1 and 12
 * @param {Number} args.value - The number of the month, between 1 and 12
 * @returns {String} - The full name of the corresponding month
 */
const monthName = ({ value }) => {
    if (!value) {
        return '';
    }
    if (typeof value !== 'number' || value < 1 || value > 12) {
        throw Error(`The value passed to monthName must be a number between 1 and 12 inclusive, received: ${value}`);
    }
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return value && months[value - 1];
};

/**
 * Return the currentYear +/- 0-n
 * @param {Object} args - An optional action to perform on the current year - either plus or minus
 * @param {String} args.action - An optional action to perform on the current year - either plus or minus
 * @param {String} args.actionValue - An optional value to add or subtract from the currentYear
 * @param {Date} currentDate - The current date, used for unit testing
 * @returns {String} - The currentYear +/- 0-n
 */
const currentYear = ({ action, actionValue }, currentDate = new Date()) => {
    const year = currentDate.getFullYear();
    if (!action || !actionValue) {
        return String(year);
    }

    if (!['plus', 'minus'].includes(action)) {
        throw Error(`invalid action: ${action} provided to currentYear transform`);
    }

    const value = parseInt(actionValue, 10);
    if (isNaN(value)) {
        throw Error(`invalid value: ${value} provided to currentYear transform`);
    }

    if (action === 'plus') {
        return String(year + value);
    }
    return String(year - value);
};

/**
 * Take a series of values from state that could be 'Y', 'N', '' or null, convert to boolean and OR the resulting booleans
 * Currently used for HnC to check that at least one security feature has been defined
 * @param {Object} args - The arguments
 * @param {Array} args.value - The array of values for the configured stateKeys
 * @returns {Boolean} - True if at least one of the values in value corresponds to true, false otherwise
 */
const booleansFromYN = ({ value }) => {
    if (value === undefined || !value.length) {
        throw Error(`The value passed to booleansFromYN needs to be a defined iterable, received: ${value}`);
    }

    const mappings = {
        Y: true,
        N: false,
        '': false,
        null: false
    };
    let result = false;
    for (const stateValue of value) {
        result = result || mappings[stateValue];
    }
    return result;
};

/**
 * Convert each word in a string to title case
 * @param {Object} args - The arguments
 * @param {String} args.value - The string to convert to title case
 * @returns {String} - args.value with title case applied
 */
const titleCase = ({ value }) => {
    if (!value) {
        return '';
    }
    return typeof value !== 'string'
        ? value
        : value.replace(/\w\S*/g, (txt) => {
            return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
        });
};

/**
 * Transform to convert a YYYY-MM-DD string to DD/MM/YYYY
 * @param {String} value The date value
 * @returns {*} An empty string if not yet set, or a DD-MM-YYYY date
 */
const displayDate = ({ value }) => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
        // safeguard only - date should be valid by the time it gets to here
        return value;
    }
    return `${pad(date.getDate(), 2)}/${pad(date.getMonth() + 1, 2)}/${date.getFullYear()}`;
};

/**
 * todo: this is used for home currently in the edit details to handle whether to display portable items or not.
 * It could possibly be replaced with the lengthEquals rule?
 * @param {Array|List|Map} value The value
 * @returns {Boolean} True or false
 */
const isEmpty = ({ value }) => {
    if (!value) {
        return true;
    }
    if (value.length === undefined && value.size === undefined) {
        throw Error(`Called isEmpty transform on a non collection value: ${value}`);
    }
    return (value.length || value.size) === 0;
};

/**
 * Execute a given transform markup string
 * @param {String} transformName - The full transform name, including the prefix and any actions and values -
 * eg transform:currentYear:Minus:1 or
 *    transform:age:state:path.to.dob.state
 * @param {Object} state - The app state, required for transforms where the action is 'state'
 * @param {String} [value] - A default value for the transform (optional)
 * @returns {String} - The transformed value
 */
export const executeTransform = (transformName, state, value) => {
    if (!transformName.startsWith('transform:')) {
        throw Error(`invalid transformName: ${transformName} passed to executeTransform`);
    }
    const [prefix, baseTransformName, action, actionValue] = transformName.split(':');

    const verticalSelectors = pageContext.getProps().verticalSelectors;
    const allTransforms = { ...transforms, ...verticalSelectors };
    if (!allTransforms[baseTransformName]) {
        throw Error(`transform: ${baseTransformName} was specified for executeTransform, but it doesn't exist`);
    }
    return allTransforms[baseTransformName]({ state, value, action, actionValue });
};

/**
 * Execute a given selector markup string
 * @param {String} selectorName - The full selector name, including the prefix and any actions and values -
 * eg selector:noClaimDiscountToNumber:state:vertical.quote.some.field
 * @param {Object} state - The app state, required for selector where the action is 'state'
 * @param {String} [value] - A default value for the transform (optional)
 * @returns {String} - The transformed value
 */
export const executeSelector = (selectorName, state, value) => {
    if (!selectorName.startsWith('selector:')) {
        throw Error(`invalid selectorName: ${selectorName} passed to executeSelector`);
    }
    const selectors = pageContext.getProps().verticalSelectors;
    const [prefix, baseSelectorName] = selectorName.split(':');

    if (!selectors[baseSelectorName]) {
        throw Error(`selector: ${baseSelectorName} was specified for executeSelector, but it doesn't exist`);
    }

    return selectors[baseSelectorName]({ state, value });
};

/**
 * @TODO: This may be unnecessary -- may be able to use dateRange using filterValue: min: { days: 1 }?
 * Returns true if date passed is greater than today's date
 * @param {Object} args - The arguments
 * @param {Object} args.state - The state
 * @param {String} args.actionValue - The action value
 * @returns {Boolean} - The age is past today
 */
const pastToday = ({ state, actionValue }) => {
    if (!actionValue) {
        return false;
    }
    const value = state.getIn(actionValue.split('.'));
    if (value === null || value === undefined) {
        return false;
    }

    const date = new Date();
    const today = {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate()
    };

    let [year, month, day] = value.split('-');
    year = Number(year);
    month = Number(month);
    day = Number(day);
    if (isNaN(day)) {
        // this is when we have to validate against a date that is only month and year - like medicare expiry
        // in this case, use the last day of the specified month as the day
        // I'm pretty sure that the preconditions on date field validation means that we won't try to validate a date
        // until all the fields have been touched and a value selected - this condition should only be hit when the field
        // doesn't contain a day field.
        day = [31, year % 4 === 0 ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
    }
    if (today.year > year) {
        return false;
    }
    if (today.month > month && year === today.year) {
        return false;
    }
    return !(today.day >= day && month === today.month && year === today.year);
};

/**
 * Translate boolean string code to readable String
 * @param {string} value Value passed
 * @return {string} Label string
 */
export const displayBooleanString = (value) => ({
    true: 'Yes',
    false: 'No',
    YES: 'Yes',
    Y: 'Yes',
    NO: 'No',
    N: 'No',
    UNSURE: 'Unsure',
    '': 'No',
    null: 'No'
}[value]);

/**
 * For each transform you create, you must export it here as well.
 * Note: different transforms may require different argument signatures.
 * @type {*}
 */
export const transforms = {
    age: getAge,
    dateValue: getDateValue,
    applyGST,
    mock: mockGetAge,
    sum: getSum,
    toNumber: getNumber,
    currentMonth,
    displayDate,
    displayBooleanString,
    isEmpty,
    monthName,
    currentYear,
    pastToday,
    booleansFromYN,
    titleCase
};
